import React, { useRef, useState } from "react";
import {
    Box,
    Typography,
    useTheme,
    Button,
    Tooltip,
    TextField,
    IconButton,
    Collapse,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Chip,
    CircularProgress,
} from "@mui/material";

import SaveIcon from "@mui/icons-material/Save";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import EditIcon from "@mui/icons-material/Edit";
import LocalPrintshopTwoToneIcon from "@mui/icons-material/LocalPrintshopTwoTone";
import InstagramIcon from "@mui/icons-material/Instagram";
import UploadIcon from "@mui/icons-material/Upload";
import KeyIcon from "@mui/icons-material/Key";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CloseIcon from "@mui/icons-material/Close";

import {
    faTriangleExclamation,
    faCircleCheck,
    faFileArchive,
    faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { toast } from "react-hot-toast";
import dayjs from "dayjs";

import { tokens } from "../../theme";
import { djangoApi } from "../../utils/axios/axios.utils";
import { setSellers } from "../../store/sellers/sellers.action";
import { formatPhoneNumber, formatToBRL } from "../../utils/formats";
import { comissionHandler } from "../../utils/comission-handler";
import UserAvatar from "./user-avatar";

const isValidValue = (value) => {
    return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
    );
};

const formatDisplayValue = (value) => {
    return isValidValue(value) ? String(value) : "Não informado";
};

const formatCpf = (cpf) => {
    const digits = String(cpf || "").replace(/\D/g, "");

    if (digits.length !== 11) {
        return formatDisplayValue(cpf);
    }

    return digits.replace(
        /(\d{3})(\d{3})(\d{3})(\d{2})/,
        "$1.$2.$3-$4"
    );
};

const formatCep = (cep) => {
    const digits = String(cep || "").replace(/\D/g, "");

    if (digits.length !== 8) {
        return formatDisplayValue(cep);
    }

    return digits.replace(/(\d{5})(\d{3})/, "$1-$2");
};

const copyText = async (text, successMessage) => {
    try {
        await navigator.clipboard.writeText(String(text));

        toast.success(successMessage, {
            position: "top-right",
        });
    } catch (error) {
        console.error("Erro ao copiar:", error);

        toast.error("Não foi possível copiar", {
            position: "top-right",
        });
    }
};

const SellerInfoField = ({
    label,
    value,
    copyValue,
    fullWidth = false,
    colors,
}) => {
    const hasValue = isValidValue(value);
    const displayValue = formatDisplayValue(value);

    const handleCopy = () => {
        if (!hasValue) return;

        copyText(
            copyValue ?? value,
            `${label} copiado`
        );
    };

    return (
        <Box
            sx={{
                gridColumn: fullWidth ? "1 / -1" : "auto",
                minWidth: 0,
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid",
                borderColor: colors.grey[700],
                backgroundColor: colors.pink[800],
            }}
        >
            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    marginBottom: "4px",
                    color: colors.grey[400],
                    fontWeight: 600,
                }}
            >
                {label}
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "8px",
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        minWidth: 0,
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                        userSelect: "text",
                        color: hasValue
                            ? colors.grey[100]
                            : colors.grey[500],
                        fontStyle: hasValue
                            ? "normal"
                            : "italic",
                    }}
                >
                    {displayValue}
                </Typography>

                {hasValue && (
                    <Tooltip title={`Copiar ${label.toLowerCase()}`}>
                        <IconButton
                            size="small"
                            onClick={handleCopy}
                            sx={{
                                padding: "3px",
                                flexShrink: 0,
                                color: colors.grey[300],
                            }}
                        >
                            <ContentCopyOutlinedIcon
                                sx={{ fontSize: 16 }}
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

const SellerInfoSection = ({
    title,
    children,
    colors,
}) => {
    return (
        <Box sx={{ marginBottom: "24px" }}>
            <Typography
                variant="subtitle1"
                sx={{
                    marginBottom: "10px",
                    color: colors.pink[200],
                    fontWeight: 700,
                }}
            >
                {title}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: "10px",
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

const SellerInfoDialog = ({
    open,
    onClose,
    seller,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    if (!seller) return null;

    const birthDate = seller.data_nascimento
        ? dayjs(seller.data_nascimento).format("DD/MM/YYYY")
        : null;

    const mainPhone = seller.telefone
        ? formatPhoneNumber(seller.telefone)
        : null;

    const secondaryPhone = seller.telefone_secundario
        ? formatPhoneNumber(seller.telefone_secundario)
        : null;

    const handleCopyAll = () => {
        const information = [
            `Nome: ${formatDisplayValue(seller.nome)}`,
            `ID: ${formatDisplayValue(seller.id)}`,
            `CPF: ${formatCpf(seller.cpf)}`,
            `Data de nascimento: ${formatDisplayValue(birthDate)}`,
            `Nome da mãe: ${formatDisplayValue(seller.nome_mae)}`,
            "",
            `E-mail: ${formatDisplayValue(seller.email)}`,
            `Telefone: ${formatDisplayValue(mainPhone)}`,
            `Telefone secundário: ${formatDisplayValue(
                secondaryPhone
            )}`,
            `Instagram: ${formatDisplayValue(seller.insta_link)}`,
            "",
            `Empresa: ${formatDisplayValue(seller.nome_empresa)}`,
            `Categoria: ${formatDisplayValue(
                seller.firebase_category
            )}`,
            "",
            "Endereço residencial:",
            `Logradouro: ${formatDisplayValue(
                seller.end_logradouro
            )}`,
            `Número: ${formatDisplayValue(seller.end_numero)}`,
            `Complemento: ${formatDisplayValue(
                seller.end_complemento
            )}`,
            `Bairro: ${formatDisplayValue(seller.end_bairro)}`,
            `Município: ${formatDisplayValue(
                seller.end_municipio
            )}`,
            `CEP: ${formatCep(seller.end_cep)}`,
            "",
            "Endereço comercial:",
            `Logradouro: ${formatDisplayValue(
                seller.end_logradouro_comercial
            )}`,
            `Número: ${formatDisplayValue(
                seller.end_numero_comercial
            )}`,
            `Complemento: ${formatDisplayValue(
                seller.end_complemento_comercial
            )}`,
            `Bairro: ${formatDisplayValue(
                seller.end_bairro_comercial
            )}`,
            `Município: ${formatDisplayValue(
                seller.end_municipio_comercial
            )}`,
            `CEP: ${formatCep(seller.end_cep_comercial)}`,
            "",
            "Endereço de entrega:",
            `Logradouro: ${formatDisplayValue(
                seller.end_logradouro_entrega
            )}`,
            `Número: ${formatDisplayValue(
                seller.end_numero_entrega
            )}`,
            `Complemento: ${formatDisplayValue(
                seller.end_complemento_entrega
            )}`,
            `Bairro: ${formatDisplayValue(
                seller.end_bairro_entrega
            )}`,
            `Município: ${formatDisplayValue(
                seller.end_municipio_entrega
            )}`,
            `CEP: ${formatCep(seller.end_cep_entrega)}`,
            "",
            `Observação: ${formatDisplayValue(
                seller.observacao || seller.obs_to_display
            )}`,
            `Status: ${seller.firebase_disabled
                ? "Desativada"
                : "Ativa"
            }`,
        ].join("\n");

        copyText(
            information,
            "Todas as informações foram copiadas"
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: "14px",
                    backgroundColor: colors.pink[900],
                    backgroundImage: "none",
                    border: "1px solid",
                    borderColor: colors.grey[700],
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "15px",
                    color: colors.grey[100],
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 700,
                            wordBreak: "break-word",
                        }}
                    >
                        {seller.nome || "Dados da vendedora"}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "7px",
                        }}
                    >
                        <Chip
                            size="small"
                            label={`ID ${seller.id}`}
                            variant="outlined"
                            sx={{
                                color: colors.grey[300],
                                borderColor: colors.grey[600],
                            }}
                        />

                        <Chip
                            size="small"
                            label={
                                seller.firebase_disabled
                                    ? "Desativada"
                                    : "Ativa"
                            }
                            color={
                                seller.firebase_disabled
                                    ? "error"
                                    : "success"
                            }
                        />

                        {seller?.kits_inadimplentes?.length > 0 && (
                            <Chip
                                size="small"
                                color="warning"
                                label={`${seller.kits_inadimplentes.length} pagamento(s) pendente(s)`}
                            />
                        )}
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    sx={{
                        color: colors.grey[300],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Divider
                sx={{
                    borderColor: colors.grey[700],
                }}
            />

            <DialogContent
                sx={{
                    paddingTop: "20px",
                    userSelect: "text",
                }}
            >
                <SellerInfoSection
                    title="Dados pessoais"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Nome completo"
                        value={seller.nome}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Nome da mãe"
                        value={seller.nome_mae}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="CPF"
                        value={formatCpf(seller.cpf)}
                        copyValue={seller.cpf}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Data de nascimento"
                        value={birthDate}
                        copyValue={birthDate}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Contato"
                    colors={colors}
                >
                    <SellerInfoField
                        label="E-mail"
                        value={seller.email}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Instagram"
                        value={seller.insta_link}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Telefone principal"
                        value={mainPhone}
                        copyValue={seller.telefone}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Telefone secundário"
                        value={secondaryPhone}
                        copyValue={seller.telefone_secundario}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Empresa"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Nome da empresa"
                        value={seller.nome_empresa}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Categoria"
                        value={seller.firebase_category}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Endereço residencial"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Logradouro"
                        value={seller.end_logradouro}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Número"
                        value={seller.end_numero}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Complemento"
                        value={seller.end_complemento}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Bairro"
                        value={seller.end_bairro}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Município"
                        value={seller.end_municipio}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="CEP"
                        value={
                            isValidValue(seller.end_cep)
                                ? formatCep(seller.end_cep)
                                : null
                        }
                        copyValue={seller.end_cep}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Endereço comercial"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Logradouro"
                        value={seller.end_logradouro_comercial}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Número"
                        value={seller.end_numero_comercial}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Complemento"
                        value={seller.end_complemento_comercial}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Bairro"
                        value={seller.end_bairro_comercial}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Município"
                        value={seller.end_municipio_comercial}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="CEP"
                        value={
                            isValidValue(seller.end_cep_comercial)
                                ? formatCep(
                                    seller.end_cep_comercial
                                )
                                : null
                        }
                        copyValue={seller.end_cep_comercial}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Endereço de entrega"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Logradouro"
                        value={seller.end_logradouro_entrega}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Número"
                        value={seller.end_numero_entrega}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Complemento"
                        value={seller.end_complemento_entrega}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Bairro"
                        value={seller.end_bairro_entrega}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="Município"
                        value={seller.end_municipio_entrega}
                        colors={colors}
                    />

                    <SellerInfoField
                        label="CEP"
                        value={
                            isValidValue(seller.end_cep_entrega)
                                ? formatCep(
                                    seller.end_cep_entrega
                                )
                                : null
                        }
                        copyValue={seller.end_cep_entrega}
                        colors={colors}
                    />
                </SellerInfoSection>

                <SellerInfoSection
                    title="Observações"
                    colors={colors}
                >
                    <SellerInfoField
                        label="Observação"
                        value={
                            seller.observacao ||
                            seller.obs_to_display
                        }
                        fullWidth
                        colors={colors}
                    />
                </SellerInfoSection>
            </DialogContent>

            <Divider
                sx={{
                    borderColor: colors.grey[700],
                }}
            />

            <DialogActions
                sx={{
                    padding: "12px 20px",
                    justifyContent: "space-between",
                }}
            >
                <Button
                    onClick={handleCopyAll}
                    startIcon={<ContentCopyOutlinedIcon />}
                    color="info"
                >
                    Copiar tudo
                </Button>

                <Button
                    onClick={onClose}
                    variant="contained"
                    color="primary"
                >
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const FileUpload = ({ userId }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef(null);

    const handleIconClick = () => {
        fileInputRef.current?.click();
    };

    const sendContractFile = async (contractFile) => {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("contract", contractFile);

        try {
            const res = await djangoApi.post(
                `sellers/sellers/${userId}/save_contract/`,
                formData,
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const {
                data,
                msg,
                error,
            } = res.data;

            if (res.status === 200) {
                dispatch(setSellers(data));

                Swal.fire({
                    title: "Feito!!",
                    text: msg,
                    icon: "success",
                });
            } else if (res.status === 208) {
                Swal.fire({
                    title: msg,
                    text: `${error}!!`,
                    icon: "error",
                });
            }
        } catch (err) {
            console.error("Erro ao salvar contrato:", err);

            Swal.fire({
                title: "Erro!",
                text: "Não foi possível salvar o contrato.",
                icon: "error",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];

        if (file) {
            sendContractFile(file);
        }

        event.target.value = "";
    };

    return (
        <Box
            sx={{
                display: "flex",
                justifySelf: "center",
                width: "100%",
                alignItems: "center",
                marginLeft: "20px",
            }}
        >
            <input
                type="file"
                style={{ display: "none" }}
                accept=".pdf, image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
            />

            {isLoading ? (
                <Box
                    sx={{
                        width: "100%",
                        justifyContent: "center",
                        display: "flex",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress
                        size={20}
                        sx={{
                            color: colors.pink[300],
                        }}
                    />
                </Box>
            ) : (
                <IconButton
                    color="info"
                    onClick={handleIconClick}
                >
                    <UploadIcon />
                </IconButton>
            )}
        </Box>
    );
};

const UserCard = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const {
        data,
        handleOpenEditUser,
        handleOpenContract,
        handleOpenContractWithPromissory,
        setUserData,
        setDataToExport,
        dataToExport,
    } = props;

    const [isLoadingDocument, setIsLoadingDocument] =
        useState(false);

    const [openFailureCardId, setOpenFailureCardId] =
        useState(null);

    const [openInfoDialog, setOpenInfoDialog] =
        useState(false);

    const [obsByKitId, setObsByKitId] = useState({});
    const [loadingByKitId, setLoadingByKitId] =
        useState({});
    const [editingByKitId, setEditingByKitId] =
        useState({});

    const handleEditUser = () => {
        handleOpenEditUser(data);
    };

    const handleEditUserContract = async () => {
        setIsLoadingDocument(true);

        try {
            const res = await djangoApi.post(
                `sellers/sellers/${data.id}/generate_file_url_user/`,
                {},
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                    },
                }
            );

            const {
                data: fileUrl,
                msg,
                error,
            } = res.data;

            if (res.status === 200) {
                if (fileUrl) {
                    window.open(
                        fileUrl,
                        "_blank",
                        "noopener,noreferrer"
                    );
                }

                Swal.fire({
                    title: "Feito!!",
                    text: msg,
                    icon: "success",
                });
            } else if (res.status === 208) {
                Swal.fire({
                    title: msg,
                    text: `${error}!!`,
                    icon: "error",
                });
            }
        } catch (err) {
            console.error(
                "Erro ao gerar arquivo do usuário:",
                err
            );

            Swal.fire({
                title: "Erro!",
                text: "Não foi possível gerar o arquivo.",
                icon: "error",
            });
        } finally {
            setIsLoadingDocument(false);
        }
    };

    const handlePrintContract = () => {
        handleOpenContract();
        setUserData(data);
    };

    const handlePrintContractWithPromissory = () => {
        handleOpenContractWithPromissory?.(data);
    };

    const handleSendResetViaDjango = async () => {
        const resetEmail = String(data?.email || "")
            .trim()
            .toLowerCase();

        if (!resetEmail || !resetEmail.includes("@")) {
            toast.error(
                "Usuário sem e-mail válido para reset.",
                {
                    position: "top-right",
                }
            );
            return;
        }

        const confirmed = await Swal.fire({
            title: "Enviar reset de senha?",
            text: `Será enviado para: ${resetEmail}`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Enviar",
            cancelButtonText: "Cancelar",
        }).then((result) => result.isConfirmed);

        if (!confirmed) return;

        const toastId = toast.loading(
            `Enviando redefinição para ${resetEmail}...`,
            {
                position: "top-right",
            }
        );

        try {
            const res = await djangoApi.post(
                "firebase/admin/send-password-reset/",
                {
                    email: resetEmail,
                },
                {
                    headers: {
                        Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            toast.success(
                res.data?.msg ||
                `Reset enviado para ${resetEmail}`,
                {
                    id: toastId,
                    position: "top-right",
                }
            );
        } catch (err) {
            console.error(err);

            const msg =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                "Não foi possível enviar o reset.";

            toast.error(msg, {
                id: toastId,
                position: "top-right",
            });
        }
    };

    const instaUrl = data.insta_link
        ? `https://www.instagram.com/${data.insta_link}/`
        : "";

    const isChecked = dataToExport.some(
        (item) => item.id === data.id
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns:
                        "60px 200px 300px 200px 50px 50px 50px 50px 150px 50px 50px 50px 50px 50px",
                    border: "1px solid black",
                    borderRadius: "12px",
                    alignItems: "center",
                    width: "100%",
                    minWidth: "1175px",
                    padding: "3px 20px 3px 10px",
                    marginTop: "5px",
                    backgroundColor: isChecked
                        ? "rgba(220, 220, 220, 0.1)"
                        : colors.pink[700],
                }}
            >
                <Link
                    to={`/vendedoras/${data.slug}`}
                    state={{
                        sellerData: data,
                    }}
                >
                    <UserAvatar
                        name={data.nome}
                        sellerId={data.id}
                        seller={data}
                    />
                </Link>

                <Typography
                    variant="h6"
                    color={colors.primary[100]}
                >
                    {data.nome}
                </Typography>

                <Typography
                    variant="h6"
                    color={colors.primary[100]}
                >
                    {data.email}
                </Typography>

                <Typography
                    variant="h6"
                    color={colors.primary[100]}
                >
                    {formatPhoneNumber(data.telefone)}
                </Typography>

                <FormControlLabel
                    control={
                        <Checkbox
                            color="success"
                            checked={isChecked}
                            onChange={(event) => {
                                if (event.target.checked) {
                                    setDataToExport((prev) => [
                                        ...prev,
                                        data,
                                    ]);
                                } else {
                                    setDataToExport((prev) =>
                                        prev.filter(
                                            (item) =>
                                                item.id !== data.id
                                        )
                                    );
                                }
                            }}
                        />
                    }
                />

                <Box
                    sx={{
                        justifySelf: "center",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {data?.kits_inadimplentes?.length > 0 ? (
                        <Tooltip title="Clique para ver mais detalhes">
                            <Box
                                onClick={() =>
                                    setOpenFailureCardId(
                                        openFailureCardId ===
                                            data.id
                                            ? null
                                            : data.id
                                    )
                                }
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    lineHeight: 1,
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faTriangleExclamation}
                                    color="goldenrod"
                                    style={{
                                        fontSize: "18px",
                                        display: "block",
                                    }}
                                />
                            </Box>
                        </Tooltip>
                    ) : (
                        <FontAwesomeIcon
                            icon={faCircleCheck}
                            color={colors.greenAccent[600]}
                            style={{
                                fontSize: "18px",
                                display: "block",
                            }}
                        />
                    )}
                </Box>

                <Box sx={{ justifySelf: "center" }}>
                    {data.firebase_disabled ? (
                        <RemoveDoneIcon
                            sx={{
                                color: colors.redAccent[500],
                            }}
                        />
                    ) : (
                        <DoneAllIcon color="success" />
                    )}
                </Box>

                <Box
                    sx={{
                        justifySelf: "center",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    {data.insta_link ? (
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href={instaUrl}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <InstagramIcon
                                color="info"
                                sx={{
                                    fontSize: 22,
                                    cursor: "pointer",
                                }}
                            />
                        </a>
                    ) : (
                        <InstagramIcon
                            color="primary"
                            sx={{
                                fontSize: 22,
                            }}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        justifySelf: "center",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                    }}
                >
                    <Tooltip title="Imprimir contrato">
                        <Button
                            onClick={handlePrintContract}
                            sx={{
                                minWidth: "unset",
                                padding: "6px",
                            }}
                        >
                            <LocalPrintshopTwoToneIcon
                                color="action"
                            />
                        </Button>
                    </Tooltip>

                    <Tooltip title="Contrato + nota promissória">
                        <Button
                            onClick={
                                handlePrintContractWithPromissory
                            }
                            sx={{
                                minWidth: "unset",
                                padding: "6px",
                            }}
                        >
                            <AccountBalanceWalletOutlinedIcon
                                sx={{
                                    color:
                                        colors.greenAccent[400],
                                }}
                            />
                        </Button>
                    </Tooltip>
                </Box>

                <FileUpload userId={data.id} />

                {isLoadingDocument ? (
                    <Box
                        sx={{
                            width: "100%",
                            justifyContent: "center",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <CircularProgress
                            size={20}
                            sx={{
                                color: colors.pink[300],
                            }}
                        />
                    </Box>
                ) : (
                    <Button
                        onClick={handleEditUserContract}
                        disabled={!data.contract}
                        sx={{
                            cursor: data.contract
                                ? "pointer"
                                : "default",
                            justifySelf: "center",
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faFileArchive}
                            color={
                                data.contract
                                    ? colors.greenAccent[300]
                                    : colors.primary[300]
                            }
                            size="lg"
                        />
                    </Button>
                )}

                <Button
                    onClick={handleEditUser}
                    sx={{
                        justifySelf: "center",
                    }}
                >
                    <FontAwesomeIcon
                        icon={faPenToSquare}
                        color={colors.yellow[600]}
                        size="lg"
                        style={{
                            cursor: "pointer",
                        }}
                    />
                </Button>

                <Tooltip title="Enviar e-mail de redefinição de senha">
                    <span>
                        <IconButton
                            onClick={handleSendResetViaDjango}
                            size="small"
                            sx={{
                                justifySelf: "center",
                            }}
                        >
                            <KeyIcon
                                sx={{
                                    color: colors.yellow[600],
                                }}
                            />
                        </IconButton>
                    </span>
                </Tooltip>

                <Tooltip title="Ver todas as informações">
                    <IconButton
                        onClick={() =>
                            setOpenInfoDialog(true)
                        }
                        size="small"
                        sx={{
                            justifySelf: "center",
                            color:
                                colors.blueAccent?.[400] ||
                                colors.grey[200],
                        }}
                    >
                        <InfoOutlinedIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Collapse
                in={openFailureCardId === data.id}
                timeout={300}
                unmountOnExit
            >
                <Box
                    sx={{
                        backgroundColor: colors.pink[900],
                        borderRadius: "12px",
                        marginTop: "2px",
                        border: "1px solid",
                        borderColor: colors.grey[500],
                        padding: "10px 20px",
                        marginBottom: "10px",
                        width: "100%",
                        minWidth: "1175px",
                    }}
                >
                    <Typography
                        variant="h6"
                        color={colors.yellow[100]}
                        mb={1}
                    >
                        Kits Inadimplentes
                    </Typography>

                    {data?.kits_inadimplentes
                        ?.slice()
                        .sort((a, b) =>
                            dayjs(a.pay_date).isAfter(
                                dayjs(b.pay_date)
                            )
                                ? 1
                                : -1
                        )
                        .map((kit) => {
                            const obs =
                                obsByKitId[kit.pk] ??
                                kit.osb_failure_to_pay ??
                                "";

                            const loading =
                                loadingByKitId[kit.pk] ?? false;

                            const editing =
                                editingByKitId[kit.pk] ?? false;

                            const valueToReceive =
                                kit.total_value_sold -
                                comissionHandler(
                                    kit.total_value_sold
                                ) -
                                kit.total_paid_value;

                            const handleSave = async () => {
                                setLoadingByKitId((prev) => ({
                                    ...prev,
                                    [kit.pk]: true,
                                }));

                                try {
                                    const response =
                                        await djangoApi.post(
                                            `products/kits_active/${kit.pk}/update_osb_failure_to_pay/`,
                                            {
                                                observacao: obs,
                                            },
                                            {
                                                headers: {
                                                    Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
                                                    "Content-Type":
                                                        "application/json",
                                                },
                                            }
                                        );

                                    if (
                                        response.status === 201
                                    ) {
                                        toast.success(
                                            response.data?.msg ||
                                            "Observação salva com sucesso!",
                                            {
                                                position:
                                                    "top-right",
                                            }
                                        );
                                    } else {
                                        Swal.fire({
                                            title: "Erro!",
                                            text: "Algo deu errado.",
                                            icon: "error",
                                        });
                                    }
                                } catch (error) {
                                    console.error(
                                        "Erro ao salvar observação:",
                                        error
                                    );

                                    Swal.fire({
                                        title: "Erro inesperado!",
                                        text: "Não foi possível salvar a observação.",
                                        icon: "error",
                                    });
                                } finally {
                                    setLoadingByKitId(
                                        (prev) => ({
                                            ...prev,
                                            [kit.pk]: false,
                                        })
                                    );

                                    setEditingByKitId(
                                        (prev) => ({
                                            ...prev,
                                            [kit.pk]: false,
                                        })
                                    );
                                }
                            };

                            return (
                                <Box
                                    key={kit.pk}
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(3, 150px) 1fr auto",
                                        columnGap: "20px",
                                        borderBottom:
                                            "1px solid",
                                        borderColor:
                                            colors.grey[700],
                                        padding: "5px 0",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography
                                        color={
                                            colors.grey[100]
                                        }
                                    >
                                        Kit: {kit.kit_number}
                                    </Typography>

                                    <Typography
                                        color={
                                            colors.grey[100]
                                        }
                                    >
                                        {dayjs(
                                            kit.pay_date
                                        ).format(
                                            "DD/MM/YYYY"
                                        )}
                                    </Typography>

                                    <Typography
                                        color={
                                            colors.grey[100]
                                        }
                                    >
                                        {formatToBRL(
                                            valueToReceive
                                        )}
                                    </Typography>

                                    {editing ? (
                                        <TextField
                                            size="small"
                                            variant="outlined"
                                            value={obs}
                                            onChange={(event) =>
                                                setObsByKitId(
                                                    (prev) => ({
                                                        ...prev,
                                                        [kit.pk]:
                                                            event
                                                                .target
                                                                .value,
                                                    })
                                                )
                                            }
                                            placeholder="Observação"
                                            disabled={loading}
                                            sx={{
                                                backgroundColor:
                                                    colors.grey[800],
                                                input: {
                                                    color:
                                                        colors
                                                            .grey[100],
                                                },
                                            }}
                                        />
                                    ) : (
                                        <Typography
                                            color={
                                                colors.grey[100]
                                            }
                                            sx={{
                                                wordBreak:
                                                    "break-word",
                                            }}
                                        >
                                            {obs || "—"}
                                        </Typography>
                                    )}

                                    {editing ? (
                                        <IconButton
                                            onClick={handleSave}
                                            disabled={loading}
                                            color="success"
                                        >
                                            {loading ? (
                                                <CircularProgress
                                                    size={20}
                                                    sx={{
                                                        color:
                                                            "white",
                                                    }}
                                                />
                                            ) : (
                                                <SaveIcon />
                                            )}
                                        </IconButton>
                                    ) : (
                                        <IconButton
                                            onClick={() =>
                                                setEditingByKitId(
                                                    (prev) => ({
                                                        ...prev,
                                                        [kit.pk]:
                                                            true,
                                                    })
                                                )
                                            }
                                            color="info"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                </Box>
            </Collapse>

            <SellerInfoDialog
                open={openInfoDialog}
                onClose={() =>
                    setOpenInfoDialog(false)
                }
                seller={data}
            />
        </Box>
    );
};

export default React.memo(UserCard);